// Lightweight perf instrumentation for project-switch path.
//
// USAGE
//   1. In DevTools console:   localStorage.setItem('PERF_SWITCH', '1')
//   2. Click projects in the sidebar.
//   3. After each switch finishes (paint) the table prints to console:
//        ▶ switch project-id
//        ╔═══════╤══════════════════════╤════════════════╗
//        ║   ms  │ phase                │ meta           ║
//        ╠═══════╪══════════════════════╪════════════════╣
//        ║   0.0 │ click                │ {from, to}     ║
//        ║   0.4 │ loadProjectData/in   │                ║
//        ║   0.7 │ cache/hit            │ {cards: 12}    ║
//        ║   1.1 │ setCards            │                ║
//        ║   3.5 │ render/CardsPanel    │ {cards: 12}    ║
//        ║   4.8 │ paint                │                ║
//        ║  92.3 │ revalidate/done      │ {bytes: ...}   ║
//        ╚═══════╧══════════════════════╧════════════════╝
//   4. Disable:                 localStorage.removeItem('PERF_SWITCH')
//
// Times are ms since `start()`; the relative deltas matter, not the
// absolute clock.
//
// ARCHITECTURE NOTE
// `mark(phase)` is a no-op when disabled (one boolean check). Safe to
// sprinkle anywhere on the hot path. `paint()` schedules a requestAnimationFrame
// + microtask so the recorded "paint" timestamp is the moment the browser
// actually committed the frame, not the moment React finished its render.

interface Session {
  id: string
  t0: number
  events: Array<{ t: number; phase: string; meta?: Record<string, unknown> }>
  pendingPaint: boolean
}

let cur: Session | null = null

function enabled(): boolean {
  try {
    return localStorage.getItem('PERF_SWITCH') === '1'
  } catch {
    return false
  }
}

/** Begin a new switch session. Discards any in-flight one (rapid clicks). */
export function start(id: string, meta?: Record<string, unknown>): void {
  if (!enabled()) return
  cur = { id, t0: performance.now(), events: [], pendingPaint: false }
  mark('click', meta)
}

/** Record a phase. No-op if disabled or no active session. */
export function mark(phase: string, meta?: Record<string, unknown>): void {
  if (!enabled() || !cur) return
  cur.events.push({ t: performance.now() - cur.t0, phase, meta })
}

/** Schedule a "paint" mark after the next browser frame commits.
 *  Idempotent within a session — only the first call is honored. */
export function paint(): void {
  if (!enabled() || !cur || cur.pendingPaint) return
  cur.pendingPaint = true
  const ses = cur
  // Two rAFs: first fires before paint, second after. Approximates
  // "the frame the user actually saw."
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      ses.events.push({ t: performance.now() - ses.t0, phase: 'paint' })
    })
  })
}

/** Print the current session and clear it. Call after revalidate done.
 *  The session keeps absorbing marks for `tailMs` after this is called so
 *  downstream renders triggered by the work being measured (e.g. a
 *  setState that fires its render asynchronously) are still captured.
 *
 *  The summary header shows TWO numbers because they answer different
 *  questions:
 *    paint=Xms  — when the user actually saw the new content (the
 *                 "perceived switch time"). This is what they care
 *                 about.
 *    total=Yms  — wall-clock until the flush; includes deferred /
 *                 background work AND the artificial `tailMs`. NOT the
 *                 user's perceived switch time. */
export function report(extraMeta?: Record<string, unknown>, tailMs: number = 500): void {
  if (!enabled() || !cur) return
  const ses = cur
  if (extraMeta) ses.events.push({ t: performance.now() - ses.t0, phase: 'report', meta: extraMeta })
  // Keep `cur === ses` so subsequent renderMarks land in the same trace.
  // Flush after `tailMs` to print the full picture.
  setTimeout(() => {
    if (cur === ses) cur = null
    ses.events.push({ t: performance.now() - ses.t0, phase: 'flush' })
    const rows = ses.events.map((e) => ({
      ms: Math.round(e.t * 10) / 10,
      phase: e.phase,
      meta: e.meta ? JSON.stringify(e.meta) : '',
    }))
    const paintRow = rows.find((r) => r.phase === 'paint')
    const paintStr = paintRow ? `paint=${paintRow.ms}ms` : 'no-paint'
    const total = rows[rows.length - 1]?.ms ?? 0
    // eslint-disable-next-line no-console
    console.groupCollapsed(`▶ switch ${ses.id} — ${paintStr}, total=${total}ms (incl. ${tailMs}ms tail), ${rows.length} marks`)
    // eslint-disable-next-line no-console
    console.table(rows)
    // eslint-disable-next-line no-console
    console.groupEnd()
  }, tailMs)
}

/** Convenience: stamp a render with a component name + key counts. Call
 *  this from inside the component body (NOT inside an effect — we want
 *  to know when React decided to re-render, not after commit). */
export function renderMark(component: string, meta?: Record<string, unknown>): void {
  mark(`render/${component}`, meta)
}
